import React from 'react';
import { DatabaseConfig } from '../../../types/database.type';
import EngineCard from '../components/EngineCard';
import { databaseEngines } from '../../../constants/databaseEngine';
import { useSettings } from '../../../contexts/SettingsContext';

interface Step1Props {
  config: DatabaseConfig;
  setConfig: (config: DatabaseConfig) => void;
}

const Step1EngineSelection: React.FC<Step1Props> = ({ config, setConfig }) => {
  const { isDbEngineVisible } = useSettings();
  const selectedEngine = databaseEngines.find((e) => e.id === config.engine);

  // Motores con backend real (available !== false) que además el admin dejó
  // visibles en settings (missing = visible).
  const visibleEngines = databaseEngines.filter(
    (e) => e.available !== false && isDbEngineVisible(e.id)
  );
  const byCategory = (cat: 'relational' | 'nosql' | 'managed') =>
    visibleEngines.filter((e) => e.category === cat);

  const handleEngineSelect = (engineId: string) => {
    const engine = databaseEngines.find((e) => e.id === engineId);
    if (engine) {
      setConfig({
        ...config,
        engine: engineId,
        version: engine.versions[0],
        port: engine.defaultPort,
        instanceType: '',
        // MongoDB on EC2 doesn't support RDS-style backups or Multi-AZ
        backups: engineId === 'mongodb' ? false : config.backups,
        multiAZ: engineId === 'mongodb' ? false : config.multiAZ,
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Database Name *</label>
        <input
          type="text"
          value={config.name}
          onChange={(e) => setConfig({ ...config, name: e.target.value })}
          placeholder="my-database"
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none"
        />
        <p className="text-xs text-gray-500 mt-1">Choose a unique identifier for your database</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Database Engine *</label>

        {/* Relational Databases */}
        {byCategory('relational').length > 0 && (
          <div className="mb-6">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Relational Databases
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {byCategory('relational').map((engine) => (
                <EngineCard
                  key={engine.id}
                  engine={engine}
                  selected={config.engine === engine.id}
                  onClick={() => handleEngineSelect(engine.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* NoSQL Databases */}
        {byCategory('nosql').length > 0 && (
          <div className="mb-6">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              NoSQL Databases
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {byCategory('nosql').map((engine) => (
                <EngineCard
                  key={engine.id}
                  engine={engine}
                  selected={config.engine === engine.id}
                  onClick={() => handleEngineSelect(engine.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Managed Databases */}
        {byCategory('managed').length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Managed Cloud Databases
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {byCategory('managed').map((engine) => (
                <EngineCard
                  key={engine.id}
                  engine={engine}
                  selected={config.engine === engine.id}
                  onClick={() => handleEngineSelect(engine.id)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {config.engine && selectedEngine && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Version *</label>
          <select
            value={config.version}
            onChange={(e) => setConfig({ ...config, version: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:ring-2 focus:ring-black focus:border-transparent outline-none"
          >
            {selectedEngine.versions.map((version) => (
              <option key={version} value={version}>
                {selectedEngine.name} {version}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default Step1EngineSelection;